"use client";

import { useEffect, useState } from "react";
import BookingLink from "./BookingLink";
import { openDiscovery } from "./open-discovery";
import { HERO_CTA_ID } from "./ProfileHeroCta";

/**
 * Phone-only bottom bar on a therapist profile: books this therapist, in the
 * spot where the site-wide dock usually sits (the header hides that dock on
 * profiles). It appears once the hero's own button has scrolled away, so the
 * two never show at once. When the therapist offers the free intro it opens
 * that form on this page (with hours: the time picker; without: the contact
 * request), otherwise the paid first session.
 */
export default function ProfileStickyCta({
  firstName,
  discovery,
  bookingUrl,
}: {
  firstName: string | null;
  /** "slots": intro with a time picker; "contact": intro by contact request. */
  discovery: "slots" | "contact" | null;
  bookingUrl: string;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // With the hero button on the page, show the bar once it has scrolled
    // up out of view. Therapists without the free intro have no hero button,
    // so the bar falls back to a fixed scroll depth.
    const hero = document.getElementById(HERO_CTA_ID);
    if (hero && typeof IntersectionObserver !== "undefined") {
      const io = new IntersectionObserver(([entry]) => {
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      io.observe(hero);
      return () => io.disconnect();
    }
    const onScroll = () => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const name = firstName ?? "terapeutul";
  const buttonClass =
    "flex h-12 w-full flex-col items-center justify-center rounded-xl bg-[#DC7050] text-[15px] font-semibold leading-tight text-white shadow-[0_4px_16px_rgba(220,112,80,0.40)] active:scale-[0.98]";

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-[58] px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] transition-all duration-300 md:hidden ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-6 opacity-0"
      }`}
    >
      <div className="mx-auto max-w-md rounded-2xl border border-white/10 bg-[#264653]/92 p-2 shadow-[0_12px_40px_rgba(38,70,83,0.45)] backdrop-blur-xl">
        {discovery ? (
          <button
            type="button"
            onClick={() => openDiscovery("profile_sticky_cta")}
            className={buttonClass}
          >
            <span>{discovery === "slots" ? "Programează ședința de cunoaștere" : "Cere o ședință de cunoaștere"}</span>
            <span className="text-[11px] font-normal text-white/85">
              {discovery === "slots" ? `cu ${name} · gratuit, online` : "gratuit · 20 de minute · online"}
            </span>
          </button>
        ) : (
          <BookingLink href={bookingUrl} location="profile_sticky_cta" className={buttonClass}>
            <span>Rezervă o ședință cu {name}</span>
          </BookingLink>
        )}
      </div>
    </div>
  );
}
